import React, { useState } from 'react';
import { useApi } from '../../apiHelpers';
import { useToast } from '../../components/Toast/ToastContext';
import { DataTable } from '../../components/table/DataTable';
import Panel from '../../components/ui/Panel';
import EmptyState from '../../components/ui/EmptyState';
import { IconButton, buttonClass } from '../../components/ui/Button';
import Tooltip from '../../components/ui/Tooltip';
import Tag from '../../components/ui/Tag';
import Reveal from '../../components/ui/Reveal';
import { Field } from '../../components/ui/Field';
import Select from '../../components/ui/Select';
import { CATEGORY } from '../../components/ui/categoryColors';

// ---------------------------------------------------------------------------
// The adjustment log, as shared-DataTable columns.
//
// Module scope per the DataTable contract: the array never changes identity, so
// no cell subtree remounts on a re-render. The delete handler arrives on the
// table's `meta` and is read at render time; never wrap one of these cells in
// React.memo, which would skip the parent's render and read a stale meta.
//
// No `sortable` and no accessorFn. The log is a short server-ordered feed and
// this widget has always shown it in that order; a header sort here would be a
// second answer to "what happened lately".
//
// Plain JavaScript file: the columns are object literals and the table renders
// with no type argument. The DataTable's generic exists for its .tsx consumers
// and costs this file nothing.
// ---------------------------------------------------------------------------

const ADJUSTMENT_COLUMNS = [
  {
    id: 'effective_date',
    header: 'Date',
    meta: { numeric: true, cellClass: 'text-on-surface-variant text-xs' },
    cell: ({ row }) => row.original.effective_date?.split('T')[0],
  },
  {
    id: 'adjustment_type',
    header: 'Type',
    cell: ({ row }) => (
      <Tag
        size="sm"
        tone={row.original.adjustment_type === 'internal_fleet' ? 'accent' : 'neutral'}
        hex={row.original.adjustment_type === 'fleet_add' ? CATEGORY.green.ink.hex : row.original.adjustment_type === 'stock_add' ? CATEGORY.blue.ink.hex : undefined}
      >{row.original.adjustment_type.replace(/_/g, ' ')}</Tag>
    ),
  },
  {
    id: 'ls_drone_count',
    header: 'Std',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => row.original.ls_drone_count || '\u2014',
  },
  {
    id: 'pyro_drone_count',
    header: 'Pyro',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => row.original.pyro_drone_count || '\u2014',
  },
  {
    id: 'battery_count',
    header: 'Batt',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => row.original.battery_count || '\u2014',
  },
  {
    id: 'flag_count',
    header: 'Flags',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => row.original.flag_count || '\u2014',
  },
  {
    id: 'notes',
    header: 'Notes',
    meta: { cellClass: 'text-on-surface-variant max-w-xs truncate text-xs' },
    cell: ({ row }) => row.original.notes || '\u2014',
  },
  {
    id: 'firstname',
    header: 'By',
    meta: { cellClass: 'text-on-surface-variant text-xs' },
    cell: ({ row }) => row.original.firstname || '\u2014',
  },
  {
    // No header label: the icon says what it does and the button carries its
    // own accessible name.
    id: 'delete',
    header: '',
    cell: (info) => {
      const { onDelete } = info.table.options.meta;
      return (
        <Tooltip label="Delete adjustment"><IconButton size="sm" onClick={() => onDelete(info.row.original.id)} label="Delete adjustment">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </IconButton></Tooltip>
      );
    },
  },
];

const FleetAdjustmentsModule = ({ adjustments, onRefresh }) => {
  const api = useApi();
  const toast = useToast();
  const [showAdjForm, setShowAdjForm] = useState(false);
  const [adjForm, setAdjForm] = useState({
    adjustment_type: 'fleet_note',
    ls_drone_count: '',
    pyro_drone_count: '',
    battery_count: '',
    flag_count: '',
    notes: '',
    effective_date: new Date().toISOString().split('T')[0],
  });

  const handleCreateAdj = async (e) => {
    e.preventDefault();
    try {
      await api.post('/dashboard/adjustments', {
        body: {
          ...adjForm,
          ls_drone_count: parseInt(adjForm.ls_drone_count) || 0,
          pyro_drone_count: parseInt(adjForm.pyro_drone_count) || 0,
          battery_count: parseInt(adjForm.battery_count) || 0,
          flag_count: parseInt(adjForm.flag_count) || 0,
        },
      });
      toast.success('Adjustment logged');
      setShowAdjForm(false);
      setAdjForm({ adjustment_type: 'fleet_note', ls_drone_count: '', pyro_drone_count: '', battery_count: '', flag_count: '', notes: '', effective_date: new Date().toISOString().split('T')[0] });
      onRefresh();
    } catch (error) {
      toast.error('Failed to create adjustment');
    }
  };

  const handleDeleteAdj = async (id) => {
    if (!window.confirm('Delete this adjustment?')) return;
    try {
      await api.delete(`/dashboard/adjustments/${id}`);
      toast.success('Deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <Panel
      title="Fleet Adjustments"
      titleStyle="section"
      accent="primary"
      level={3}
      actions={(
        <button
          onClick={() => setShowAdjForm(!showAdjForm)}
          className={buttonClass(showAdjForm ? 'tonal' : 'accent', 'sm')}
        >
          {showAdjForm ? 'Cancel' : '+ Log Entry'}
        </button>
      )}
    >
      <div className="text-xs text-on-surface-variant mb-4 space-y-1">
        <p>Manual log for tracking fleet changes: entries here are <span className="text-on-surface-variant">informational only</span> and do not modify inventory or drone records.</p>
        <p>Use <span style={{ color: CATEGORY.green.ink.oklch }}>Fleet Add</span> when new drones join the fleet, <span style={{ color: CATEGORY.blue.ink.oklch }}>Stock Add</span> when parts or batteries arrive, or <span className="text-on-surface-variant">Note</span> for general operational updates. Each entry is timestamped with who logged it and when.</p>
      </div>

      <Reveal show={showAdjForm}>
        <Panel on="panel" label="New adjustment" className="mb-5">
        <form onSubmit={handleCreateAdj} className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] text-on-surface-variant uppercase tracking-wider font-medium">Type</label>
              <div className="mt-1">
                <Select
                  label="Type"
                  value={adjForm.adjustment_type}
                  onChange={key => setAdjForm({ ...adjForm, adjustment_type: key })}
                  options={[
                    { key: 'fleet_add', label: 'Fleet Add' },
                    { key: 'stock_add', label: 'Stock Add' },
                    { key: 'fleet_note', label: 'Note' },
                  ]}
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] text-on-surface-variant uppercase tracking-wider font-medium">Date</label>
              <Field type="date" value={adjForm.effective_date} onChange={e => setAdjForm({ ...adjForm, effective_date: e.target.value })} className="mt-1" />
            </div>
            <div>
              <label className="text-[10px] text-on-surface-variant uppercase tracking-wider font-medium">Standard</label>
              <Field type="number" value={adjForm.ls_drone_count} onChange={e => setAdjForm({ ...adjForm, ls_drone_count: e.target.value })} className="mt-1" />
            </div>
            <div>
              <label className="text-[10px] text-on-surface-variant uppercase tracking-wider font-medium">Pyro Drones</label>
              <Field type="number" value={adjForm.pyro_drone_count} onChange={e => setAdjForm({ ...adjForm, pyro_drone_count: e.target.value })} className="mt-1" />
            </div>
            <div>
              <label className="text-[10px] text-on-surface-variant uppercase tracking-wider font-medium">Batteries</label>
              <Field type="number" value={adjForm.battery_count} onChange={e => setAdjForm({ ...adjForm, battery_count: e.target.value })} className="mt-1" />
            </div>
            <div>
              <label className="text-[10px] text-on-surface-variant uppercase tracking-wider font-medium">Flags</label>
              <Field type="number" value={adjForm.flag_count} onChange={e => setAdjForm({ ...adjForm, flag_count: e.target.value })} className="mt-1" />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-on-surface-variant uppercase tracking-wider font-medium">Notes</label>
            <Field type="text" value={adjForm.notes} onChange={e => setAdjForm({ ...adjForm, notes: e.target.value })}
              placeholder="What changed?" className="mt-1" />
          </div>
          <button type="submit"
            className={buttonClass('filled')}>
            Save Entry
          </button>
        </form>
        </Panel>
      </Reveal>

      <DataTable
        data={adjustments}
        columns={ADJUSTMENT_COLUMNS}
        getRowId={(adj) => String(adj.id)}
        meta={{ onDelete: handleDeleteAdj }}
        on="panel"
        empty={<EmptyState variant="area">No adjustments logged</EmptyState>}
      />
    </Panel>
  );
};

export default FleetAdjustmentsModule;
